import React, { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Target } from 'lucide-react';

/**
 * Mission statement banner — slowly rotates through English, Hindi & Marathi.
 * Auto-advances every 6s (paused under prefers-reduced-motion); dots let the
 * visitor pick a language directly.
 */
const MISSIONS = [
    {
        lang: 'English',
        heading: 'Our Mission',
        text: 'To awaken true traffic sense in every citizen. Breaking the rules of the road should never be an everyday habit — let each of us pause and think ten times before it. Our mission will be complete only when we see people honour traffic rules with sincerity and pride.',
    },
    {
        lang: 'हिंदी',
        heading: 'हमारा ध्येय',
        text: 'हर नागरिक के मन में सच्ची यातायात समझ जगाना ही हमारा ध्येय है। नियम तोड़ना कभी आम आदत न बने — ऐसा करने से पहले हर किसी को दस बार सोचना चाहिए। हमारा मिशन तभी पूरा होगा जब हम लोगों को पूरी ईमानदारी और गर्व के साथ यातायात नियमों का पालन करते देखेंगे।',
    },
    {
        lang: 'मराठी',
        heading: 'आमचे ध्येय',
        text: 'प्रत्येक नागरिकाच्या मनात खरी वाहतूक समज जागवणे हेच आमचे ध्येय आहे. नियम मोडणे ही कधीही रोजची सवय होऊ नये — असे करण्यापूर्वी प्रत्येकाने दहा वेळा विचार करावा. लोक प्रामाणिकपणे आणि अभिमानाने वाहतूक नियम पाळताना दिसतील, तेव्हाच आमचे ध्येय पूर्ण होईल.',
    },
];

export default function Mission() {
    const reduce = useReducedMotion();
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (reduce) return;
        const id = setInterval(() => setIndex((p) => (p + 1) % MISSIONS.length), 6000);
        return () => clearInterval(id);
    }, [reduce]);

    const active = MISSIONS[index];

    return (
        <section
            id="mission"
            className="scroll-mt-20 relative overflow-hidden bg-primary text-primary-foreground"
            aria-labelledby="mission-title"
        >
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80" />
            <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
                <div className="flex items-center justify-center gap-2 mb-6">
                    <Target className="w-5 h-5" aria-hidden="true" />
                    <span className="text-xs uppercase tracking-[0.2em] font-medium opacity-90">
                        {active.lang}
                    </span>
                </div>

                {/* key re-mounts the block on each change so the entrance animation replays */}
                <div key={index} className="animate-mission space-y-4 min-h-[200px] sm:min-h-[170px]">
                    <h2 id="mission-title" className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
                        {active.heading}
                    </h2>
                    <p className="text-base sm:text-lg leading-relaxed max-w-3xl mx-auto opacity-95">
                        {active.text}
                    </p>
                </div>

                {/* Language indicator / picker */}
                <div className="flex items-center justify-center gap-2 mt-8">
                    {MISSIONS.map((m, idx) => (
                        <button
                            key={m.lang}
                            onClick={() => setIndex(idx)}
                            aria-label={`Show mission in ${m.lang}`}
                            aria-current={idx === index}
                            className={`h-2 rounded-full transition-all duration-300 ${
                                idx === index
                                    ? 'w-8 bg-primary-foreground'
                                    : 'w-2 bg-primary-foreground/40 hover:bg-primary-foreground/60'
                            }`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
